/**
 * Centralized chart color palettes for analytics components.
 *
 * Previously each analytics component defined its own near-identical hex maps
 * (WO_STATUS_COLORS, STATUS_COLORS, PROJECT_STATUS_COLORS, BAR_COLORS,
 * USER_COLORS, CREATOR_COLORS …). They drifted apart and were duplicated. Import
 * from here instead so status colors stay consistent across every chart.
 */

/** Color used when a status/category has no explicit mapping. */
export const CHART_FALLBACK_COLOR = '#94a3b8';

/** Work order status → color. Keys match backend status slugs. */
export const WORK_ORDER_STATUS_COLORS: Record<string, string> = {
    registered: '#3b82f6',
    in_progress: '#f97316',
    proposal_sent: '#6366f1',
    completed: '#22c55e',
    canceled: '#ef4444',
    cancelled: '#ef4444',
};

/** Work order priority → color. Keys match config.json priority slugs. */
export const PRIORITY_COLORS: Record<string, string> = {
    urgent: '#dc2626',
    high: '#f59e0b',
    medium: '#3b82f6',
    low: '#22c55e',
};

/** Project task status → color. Keys match config.json projectTaskStatuses slugs. */
export const TASK_STATUS_COLORS: Record<string, string> = {
    'not-started': '#6b7280',
    not_started: '#6b7280',
    'in-progress': '#3b82f6',
    in_progress: '#3b82f6',
    completed: '#22c55e',
    blocked: '#ef4444',
};

/** Project status → color. Keys match backend status slugs (both spellings of "cancel"). */
export const PROJECT_STATUS_COLORS: Record<string, string> = {
    active: '#3b82f6',
    completed: '#22c55e',
    on_hold: '#f97316',
    cancelled: '#ef4444',
    canceled: '#ef4444',
    draft: '#94a3b8',
    planning: '#8b5cf6',
    in_progress: '#06b6d4',
};

/**
 * Categorical palette for series without an inherent status color
 * (per-company, per-employee, per-creator bars/slices). Indexed cyclically.
 */
export const CATEGORICAL_PALETTE = ['#2563eb', '#16a34a', '#dc2626', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#65a30d', '#ea580c', '#4f46e5'];

/** Look up a status color with a safe fallback. */
export function statusColor(map: Record<string, string>, key: string | undefined): string {
    return (key ? map[key] : undefined) ?? CHART_FALLBACK_COLOR;
}

/** Pick a categorical color by index (wraps around). */
export function categoricalColor(index: number): string {
    return CATEGORICAL_PALETTE[index % CATEGORICAL_PALETTE.length];
}
